import type { Lead } from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';

import { downloadVcfFile } from '@/core/lib/download-vcf';
import {
  buildVcfFromLead,
  leadHasVcfContent,
  leadVcfFilename,
} from '@/core/lib/leads-vcf';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

export type SaveLeadToDeviceResult =
  | { outcome: 'contacts'; contactId: string }
  | { outcome: 'cancelled' }
  | { outcome: 'vcf' }
  | { outcome: 'empty' };

/**
 * Prefer the native “create contact” draft UI when available so the user can
 * review/edit before saving. Otherwise download/share a vCard.
 * Web always uses the vCard path (no fake Contacts API).
 */
export async function saveLeadToDevice(
  lead: Lead,
): Promise<SaveLeadToDeviceResult> {
  if (!leadHasVcfContent(lead)) {
    return { outcome: 'empty' };
  }

  if (isCapacitorNative()) {
    const draftResult = await tryPresentNativeContactDraft(lead);
    if (draftResult) {
      return draftResult;
    }
  }

  await downloadVcfFile(buildVcfFromLead(lead), leadVcfFilename(lead));
  return { outcome: 'vcf' };
}

/** Explicit vCard download / share (secondary action). */
export async function shareLeadVcf(
  lead: Lead,
): Promise<SaveLeadToDeviceResult> {
  if (!leadHasVcfContent(lead)) {
    return { outcome: 'empty' };
  }
  await downloadVcfFile(buildVcfFromLead(lead), leadVcfFilename(lead));
  return { outcome: 'vcf' };
}

function leadToContactPayload(lead: Lead) {
  const givenName = lead.firstName?.trim() || '';
  const familyName = lead.lastName?.trim() || '';
  const formattedName =
    formatLeadDisplayName(lead.firstName, lead.lastName) ||
    lead.email?.trim() ||
    lead.phone?.trim() ||
    'Contact';

  const noteParts: string[] = [];
  if (lead.message?.trim()) noteParts.push(lead.message.trim());
  if (lead.description?.trim()) noteParts.push(lead.description.trim());

  return {
    givenName: givenName || formattedName,
    familyName: familyName || undefined,
    fullName: formattedName,
    organizationName: lead.companyName?.trim() || undefined,
    note: noteParts.length > 0 ? noteParts.join('\n\n') : undefined,
    emailAddresses: lead.email?.trim()
      ? [{ value: lead.email.trim(), type: 'WORK' as const, isPrimary: true }]
      : undefined,
    phoneNumbers: lead.phone?.trim()
      ? [{ value: lead.phone.trim(), type: 'WORK' as const, isPrimary: true }]
      : undefined,
    postalAddresses: lead.address?.trim()
      ? [
          {
            street: lead.address.trim(),
            type: 'WORK' as const,
            isPrimary: true,
          },
        ]
      : undefined,
  };
}

/**
 * Opens the system create-contact sheet (pre-filled). User edits and saves —
 * or cancels. Does not silently write into the address book.
 *
 * Android uses ACTION_INSERT (no WRITE_CONTACTS needed).
 * iOS uses CNContactViewController(forNewContact:); the OS may still prompt
 * for Contacts access when the user taps Done.
 */
async function tryPresentNativeContactDraft(
  lead: Lead,
): Promise<SaveLeadToDeviceResult | null> {
  try {
    const { CapacitorContacts } = await import('@capgo/capacitor-contacts');

    const supported = await CapacitorContacts.isSupported();
    if (!supported.isSupported) return null;

    const available = await CapacitorContacts.isAvailable();
    if (!available.isAvailable) return null;

    const result = await CapacitorContacts.displayCreateContact({
      contact: leadToContactPayload(lead),
    });

    if (result.id) {
      return { outcome: 'contacts', contactId: result.id };
    }

    return { outcome: 'cancelled' };
  } catch (error) {
    console.error('Native contact draft failed, falling back to vCard', error);
    return null;
  }
}
